import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, GraduationCap, MapPin, Calendar } from 'lucide-react';
import { certifications, education } from '../data/mock';
import { SECTIONS, createSectionVariants } from '../lib/motionDirector';

const Certifications = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // Motion Director - Quick, supporting content
  const variants = createSectionVariants(SECTIONS.certifications);

  return (
    <section id="certifications" className="relative py-40 bg-zinc-900 overflow-hidden" ref={ref}>
      {/* Enhanced golden-honey ambient glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-800/22 via-yellow-900/10 to-orange-950/12" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.12),transparent_60%)]" />
      {/* Kanji Typography Layer - Large watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-[25rem] font-bold text-amber-100/[0.05] select-none tracking-wider">功績</div>
      </div>

      {/* Japanese background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-10 text-[18rem] font-bold text-amber-100/[0.02] select-none">誉</div>
        <div className="absolute bottom-40 left-20 text-[12rem] font-bold text-red-900/[0.03] select-none">学</div>
        
        {/* Torii gate silhouette */}
        <svg className="absolute bottom-0 right-0 w-96 h-64 opacity-5" viewBox="0 0 200 150">
          <path d="M20 150 L20 60 L180 60 L180 150" stroke="#8B0000" strokeWidth="8" fill="none" />
          <path d="M0 65 L200 65" stroke="#8B0000" strokeWidth="10" />
          <path d="M0 50 L200 50" stroke="#8B0000" strokeWidth="6" />
          <path d="M40 60 L40 150" stroke="#8B0000" strokeWidth="6" fill="none" />
          <path d="M160 60 L160 150" stroke="#8B0000" strokeWidth="6" fill="none" />
        </svg>
      </div>

      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        variants={variants.container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Header */}
        <motion.div
          variants={variants.item}
          className="text-center mb-24"
        >
          <div className="flex items-center justify-center gap-6 mb-6">
            <motion.div 
              className="h-px w-20 bg-gradient-to-r from-transparent via-amber-600 to-amber-700"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 1, delay: 0.2 }}
            />
            <motion.span 
              className="text-5xl text-amber-700/40 font-bold"
              animate={{ opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              誉
            </motion.span>
            <motion.div 
              className="h-px w-20 bg-gradient-to-l from-transparent via-amber-600 to-amber-700"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 1, delay: 0.2 }}
            />
          </div>
          <motion.span 
            className="inline-block px-6 py-2 text-xs tracking-[0.35em] text-amber-400 border-2 border-amber-800/50 rounded-sm mb-6 bg-black/40 backdrop-blur-sm shadow-lg shadow-amber-900/20"
            whileHover={{ scale: 1.05, borderColor: 'rgba(217, 119, 6, 0.7)' }}
          >
            HONORS & TRAINING
          </motion.span>
          <h2 className="font-bebas text-6xl md:text-8xl tracking-wider text-white drop-shadow-[0_0_20px_rgba(251,191,36,0.3)]">
            BADGES OF HONOR
          </h2>
        </motion.div>

        {/* Education Card - Omamori Talisman Style */}
        <motion.div
          variants={variants.item}
          className="mb-20 max-w-3xl mx-auto"
        >
          {/* Pendulum wrapper - swings independently */}
          <motion.div
            className="w-full"
            style={{ transformOrigin: 'top center' }}
            animate={{ 
              rotate: [3, -3, 3],
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity, 
              ease: [0.445, 0.05, 0.55, 0.95],
              times: [0, 0.5, 1]
            }}
          >
          {/* Hanging string with glow */}
          <div className="flex justify-center mb-2">
            <div 
              className="w-1.5 h-12 bg-gradient-to-b from-amber-700 to-amber-900 shadow-[0_0_10px_rgba(251,191,36,0.4)]"
            />
          </div>
          
          {/* Decorative knot with animation */}
          <div className="flex justify-center mb-2">
            <motion.div 
              className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-600 to-amber-900 border-3 border-amber-500 shadow-lg shadow-amber-900/50"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          
          {/* Omamori body with enhanced styling */}
          <motion.div 
            className="relative p-10 bg-gradient-to-br from-red-900 to-red-950 border-4 border-amber-500 overflow-hidden shadow-2xl shadow-amber-900/40" 
            style={{ borderRadius: '12px 12px 80px 80px' }}
            whileHover={{ scale: 1.02, borderColor: 'rgba(251, 191, 36, 0.9)' }}
            transition={{ duration: 0.3 }}
          >
            {/* Enhanced gold accent pattern at top */}
            <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-r from-amber-700 via-amber-400 to-amber-700 opacity-90 shadow-lg" />
            <div className="absolute top-6 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent opacity-60" />
            
            {/* Background kanji with glow */}
            <motion.span 
              className="absolute right-6 bottom-10 text-[12rem] font-bold text-amber-300/[0.12]"
              animate={{ rotate: [0, 2, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
            >
              学
            </motion.span>
            
            <div className="flex items-start gap-8 relative">
              <motion.div 
                className="p-5 bg-gradient-to-br from-amber-500/30 to-amber-600/20 rounded-xl border-2 border-amber-400/50 shadow-lg shadow-amber-900/30"
                whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.4 }}
              >
                <GraduationCap className="w-12 h-12 text-amber-200" />
              </motion.div>
              <div className="flex-1">
                <h3 className="font-bebas text-4xl tracking-wider text-white mb-3 drop-shadow-lg">
                  {education.degree}
                </h3>
                <p className="text-amber-300 text-xl mb-3 font-semibold">{education.field}</p>
                <p className="text-amber-100/90 mb-5 text-base">{education.university}</p>
                <div className="flex flex-wrap gap-8 text-sm text-amber-100/80">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-amber-400" />
                    <span className="font-medium">{education.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-amber-400" />
                    <span className="font-medium">{education.period}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Enhanced tassel at bottom */}
          <div className="flex justify-center">
            <motion.div 
              className="relative"
              animate={{ y: [0, 3, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="w-1 h-4 bg-amber-800 mx-auto shadow-[0_0_8px_rgba(251,191,36,0.4)]" />
              <div className="w-10 h-16 bg-gradient-to-b from-amber-600 via-amber-700 to-amber-900 opacity-90 shadow-lg" style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 15% 100%)' }} />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-amber-500/20 rounded-full blur-md" />
            </motion.div>
          </div>
          </motion.div>
        </motion.div>

        {/* Certifications Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.name}
              variants={variants.item}
              className="group relative p-6 bg-gradient-to-br from-zinc-900/60 to-zinc-950/80 border-2 border-amber-900/30 rounded-xl transition-all duration-300 overflow-hidden backdrop-blur-sm"
              whileHover={{ 
                borderColor: 'rgba(217, 119, 6, 0.6)',
                boxShadow: '0 20px 40px -15px rgba(217, 119, 6, 0.4), inset 0 1px 2px 0 rgba(251, 191, 36, 0.1)'
              }}
            >
              {/* Enhanced seal stamp effect */}
              <motion.div 
                className="absolute -right-3 -top-3 w-14 h-14 opacity-15 group-hover:opacity-30 transition-opacity"
                whileHover={{ rotate: 45, scale: 1.1 }}
              >
                <svg viewBox="0 0 50 50" className="w-full h-full">
                  <circle cx="25" cy="25" r="22" stroke="#D97706" strokeWidth="3" fill="none" />
                  <text x="25" y="30" textAnchor="middle" fill="#D97706" fontSize="16" fontWeight="bold">印</text>
                </svg>
              </motion.div>
              
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <motion.div 
                className="flex items-start gap-4 relative"
                whileHover={{ x: 2, y: -1 }}
                transition={{ duration: 0.15 }}
              >
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <Award className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                </motion.div>
                <div>
                  <h4 className="text-base font-semibold text-amber-50 mb-2 leading-tight group-hover:text-white transition-colors">
                    {cert.name}
                  </h4>
                  <p className="text-xs text-amber-100/50 group-hover:text-amber-100/70 transition-colors">{cert.issuer}</p>
                </div>
              </motion.div>

              <motion.div 
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-600 to-transparent origin-center"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Certifications;
